import { useUserContext } from "@context/UserContext";
import { getCurrentTicketNumber, getCustomObjects, popupError, updatePrimaryEmail, zafClient } from "@services/Zendesk";
import { useNavigate, type NavigateFunction } from "react-router-dom";
import { useMemo, useState } from "react";
import type { User, UserList, UserField } from "types/UserZendesk";
import type { FieldValue } from "types/FieldValue";
import { UserData } from "@constants/useUserData";

function useCustomFields(selectedUser: User | null, userFields: UserField[]) {
    const getFormattedCustomFieldValue = (field: UserField, value: FieldValue['value']) => {
        switch (field.type) {
            case 'checkbox':
                return value ? 'Sim' : 'Não';
            case 'multiselect':
                if (Array.isArray(value)) {
                    const options = field.custom_field_options || [];
                    return value
                        .map(v => options.find(o => o.value === v)?.name || v)
                        .join(', ');
                }
                break;
            case 'dropdown':
                if (field.custom_field_options) {
                    const option = field.custom_field_options.find(o => o.value === value);
                    return option?.name || value;
                }
                break;
            default:
                return value;
        }
        return value;
    };

    const getCustomFieldsInfo = () => {
        return userFields
            .filter(field => {
                const value = selectedUser?.user_fields?.[field.key];
                return value !== undefined && value !== null && value !== '';
            })
            .map(field => {
                const value = selectedUser?.user_fields?.[field.key];
                return {
                    label: field.title,
                    value: getFormattedCustomFieldValue(field, value)
                };
            });
    };
    return { getCustomFieldsInfo };
}
function useUserApi(selectedUser: User | null, navigate: NavigateFunction, selectedRole: string | null = null) {
    const [loading, setLoading] = useState(false);
    const saveUser = async () => {
        if (!selectedUser?.id) return;
        const ticket = await getCurrentTicketNumber(zafClient);
        try {
            setLoading(true);
            const res = await zafClient.request({
                url: `/api/v2/users/${selectedUser.id}`,
                type: 'GET',
            });

            const typedRes = res as UserList;
            const currentUser = typedRes.user;

            if (currentUser.role === 'admin') {
                popupError("Contas com papel de administrador não podem ser editadas.", true);
                return;
            }

            if (selectedUser.email && selectedUser.email !== currentUser.email) {
                await updatePrimaryEmail(selectedUser.id, selectedUser.email);
            }

            await zafClient.request({
                url: `/api/v2/users/${selectedUser.id}`,
                type: "PUT",
                data: {
                    user: {
                        name: selectedUser.name,
                        phone: selectedUser.phone,
                        external_id: selectedUser.user_fields.cpf_3?.replace(/\D/g, '') || selectedUser.user_fields.cnpj_regex?.replace(/\D/g, ''),
                        user_fields: {
                            ...selectedUser.user_fields,
                            cpf_3: selectedUser.user_fields?.cpf_3 || null,
                            cnpj_regex: selectedUser.user_fields?.cnpj_regex || null,
                        }
                    }
                }
            });

            await zafClient.request({
                url: `/api/v2/users/${selectedUser.id}/tags.json`,
                type: "PUT",
                data: {
                    tags: ["app-contas"]
                }
            });

            if (selectedRole) {
                const customObjects = await getCustomObjects(zafClient, ticket as number);

                const customObject = customObjects.find(
                    (obj: { external_id: string }) => obj.external_id === `ticket-${ticket}`
                );

                if (customObject) {
                    const currentFields = customObject.custom_object_fields || {};

                    const updatedFields = {
                        ...currentFields,
                        [selectedRole]: selectedUser.id?.toString(),
                    };

                    await zafClient.request({
                        url: customObject.url,
                        type: 'PUT',
                        data: {
                            custom_object_record: {
                                custom_object_fields: updatedFields,
                            },
                        },
                    });
                } else if (customObjects.length > 0) {
                    await zafClient.request({
                        url: `/api/v2/custom_objects/contas_do_ticket/records/${customObjects[0].id}`,
                        type: 'PUT',
                        data: {
                            custom_object_record: {
                                custom_object_id: customObjects[0].id,
                                custom_object_fields: {
                                    [selectedRole]: selectedUser.id?.toString(),
                                },
                            },
                        },
                    });
                }
            }

            if (selectedRole === 'cliente') {
                await zafClient.request({
                    url: `/api/v2/tickets/${ticket}.json`,
                    type: 'PUT',
                    data: {
                        ticket: {
                            requester_id: selectedUser.id,
                        }
                    }
                });
            }

            navigate("/user-success", { state: { user: selectedUser } });

        } catch (error) {
            const detailsError = error as any;


            if (detailsError.status === 403) {
                popupError("Você não tem permissão para editar usuários. Por favor, contate o administrador.", true);
                return;
            }

            if (detailsError?.status === 422) {
                const details = detailsError?.responseJSON?.details;
                const firstKey = details ? Object.keys(details)[0] : null;
                const message = firstKey
                    ? details[firstKey]?.[0]?.description
                    : null;

                popupError(message || "Erro ao validar dados.", true);
                return;
            }

        } finally {
            setLoading(false);
        }
    };

    return { saveUser, loading };
}


export function useReviewUser() {
    const { selectedUser, userFields } = useUserContext();
    const navigate = useNavigate();

    const { getDocumentInfo, getPhoneInfo, getEmailInfo, getNameInfo } = UserData(selectedUser);
    const { getCustomFieldsInfo } = useCustomFields(selectedUser, userFields);
    const { saveUser, loading } = useUserApi(selectedUser, navigate, selectedUser?.role || null);

    const info = useMemo(() => [
        getNameInfo(),
        getDocumentInfo(),
        getPhoneInfo(),
        getEmailInfo(),
        ...getCustomFieldsInfo()
    ], [selectedUser, userFields]);

    return {
        selectedUser,
        handleSave: saveUser,
        info,
        navigate,
        loading
    };
}
